import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import type { OpsPerson } from "../../shared/organization-overview-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { searchPeople } from "./api";
import type { OperationsNavigate } from "./navigation";
import { OperationsLink } from "./OperationsLink";

const ROLE_LABEL = {
  platform_admin: "Platform admin",
  organization_admin: "Workspace admin",
  member: "Member",
} as const;

function PersonCard({ navigate, person }: { navigate: OperationsNavigate; person: OpsPerson }) {
  return (
    <li className="rounded-sm border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="font-semibold">{person.displayName}</p>
          <p className="text-sm text-muted-foreground">{person.email ?? person.username}</p>
        </div>
        {person.accountStatus === "disabled" ? (
          <Badge variant="destructive">Account disabled</Badge>
        ) : null}
      </div>
      {person.organizations.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          No workspace this employee account may read.
        </p>
      ) : (
        <ul className="mt-3 space-y-1">
          {person.organizations.map((organization) => (
            <li
              className="flex flex-wrap items-center gap-2 text-sm"
              key={organization.organizationId}
            >
              <OperationsLink
                className="font-semibold text-link hover:underline"
                href={`/customers/${organization.slug}/people`}
                navigate={navigate}
              >
                {organization.name}
              </OperationsLink>
              <span className="text-muted-foreground">{ROLE_LABEL[organization.role]}</span>
              {organization.membershipStatus === "disabled" ? (
                <Badge variant="destructive">Suspended here</Badge>
              ) : null}
              {organization.organizationStatus === "active" ? null : (
                <Badge variant="destructive">Workspace {organization.organizationStatus}</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Find a person across every customer this employee may read.
 *
 * The entry point for the common support case: someone writes in from an address and the employee
 * has to work out which workspaces they belong to before opening any of them. Results are bounded
 * and every organization listed is one this employee is already authorized for, so the search
 * cannot be used to discover customers outside their assignments.
 */
export function PeopleSearch({ navigate }: { navigate: OperationsNavigate }) {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const people = useQuery({
    queryKey: ["ops", "people", query],
    queryFn: () => searchPeople(query),
    enabled: query.length >= 2,
  });

  function search(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setQuery(searchInput.trim());
  }

  const rows = people.data?.people ?? [];
  return (
    <div>
      <form className="mt-4 flex flex-wrap gap-2" onSubmit={search} role="search">
        <Input
          aria-label="Search people by email or name"
          className="min-w-60 flex-1"
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
          placeholder="Email address or name"
          value={searchInput}
        />
        <Button type="submit">
          <Search /> Search
        </Button>
      </form>
      {people.error ? (
        <div className="mt-4">
          <FieldError>{people.error.message}</FieldError>
        </div>
      ) : null}
      {query.length < 2 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Enter at least two characters of an email address or name.
        </p>
      ) : people.isPending ? (
        <InlineLoading label="Searching…" className="mt-4" />
      ) : rows.length === 0 ? (
        <p className="mt-4 rounded-sm border border-dashed p-3 text-sm text-muted-foreground">
          Nobody in a customer you can read matches “{query}”.
        </p>
      ) : (
        <>
          <ul className="mt-4 space-y-3">
            {rows.map((person) => (
              <PersonCard key={person.userId} navigate={navigate} person={person} />
            ))}
          </ul>
          {people.data?.truncated ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Showing the first {rows.length} matches. Narrow the search to see others.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
import { InlineLoading } from "../components/ContentLoading";
